/**
 * Utility functions for conversion tracking
 */

import type {
  EcommerceItem,
  SubscriptionDetails,
  TrackingParams,
} from './types'
import { GOOGLE_MEASUREMENT_ID } from '@/lib/constant'
import { extractAttributionSignals } from '@/lib/analytics/conversion/utils'
// Use unified hash functions from analytics/conversion
import {
  hashEmail as hashEmailFromConversion,
  hashPhone as hashPhoneFromConversion,
  hashString,
} from '@/lib/analytics/conversion/utils'

/**
 * Hash PII data using SHA-256
 * Required for Google Enhanced Conversions
 * @deprecated Use hashString from @/lib/analytics/conversion/utils instead
 */
export async function hashPII(value: string): Promise<string> {
  return hashString(value)
}

/**
 * Hash email for enhanced conversions
 * Re-exported from unified conversion utils
 */
export async function hashEmail(email: string): Promise<string> {
  return hashEmailFromConversion(email)
}

/**
 * Hash phone number for enhanced conversions
 * Re-exported from unified conversion utils
 */
export async function hashPhone(phone: string): Promise<string> {
  return hashPhoneFromConversion(phone)
}

/**
 * Generate standardized item_id (SKU) from subscription details
 * Format: {plan}_{credits}c_{domains}d_{billing}
 * Example: "basic_200c_1d_monthly", "premium_500c_1d_yearly"
 */
export function generateItemId(details: SubscriptionDetails): string {
  const { plan, credits, domains, billing_interval } = details
  return `${plan}_${credits}c_${domains}d_${billing_interval}`
}

/**
 * Generate human-readable item name
 * Format: {Plan} Plan - {credits} Credits/Month - {domains} Domain(s) [(Annual)]
 * Example: "Basic Plan - 200 Credits/Month - 1 Domain"
 *          "Premium Plan - 500 Credits/Month - 1 Domain (Annual)"
 */
export function generateItemName(details: SubscriptionDetails): string {
  const { plan, credits, domains, billing_interval } = details

  const planName = plan.charAt(0).toUpperCase() + plan.slice(1)
  const domainText = domains === 1 ? '1 Domain' : `${domains} Domains`
  const billingText = billing_interval === 'yearly' ? ' (Annual)' : ''

  return `${planName} Plan - ${credits} Credits/Month - ${domainText}${billingText}`
}

/**
 * Create ecommerce item from subscription details
 */
export function createEcommerceItem(
  details: SubscriptionDetails,
): EcommerceItem {
  const { amount, billing_interval } = details

  // Calculate monthly price (for yearly, divide by 12)
  const monthlyPrice = billing_interval === 'yearly' ? amount / 12 : amount

  return {
    item_id: generateItemId(details),
    item_name: generateItemName(details),
    item_category: 'workspace_subscription',
    item_variant: billing_interval,
    price: monthlyPrice,
    quantity: 1,
  }
}

/**
 * Store gclid to localStorage
 */
export function storeGclid(gclid: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem('_gclid', gclid)
    localStorage.setItem('_gclid_timestamp', Date.now().toString())
  } catch (e) {
    console.warn('Failed to store gclid:', e)
  }
}

/**
 * Get gclid from localStorage or URL
 * Google Ads gclid is valid for 90 days
 */
export function getGclid(): string | null {
  if (typeof window === 'undefined') return null

  // First check URL params
  const urlParams = new URLSearchParams(window.location.search)
  const gclidFromUrl = urlParams.get('gclid')

  if (gclidFromUrl) {
    storeGclid(gclidFromUrl)
    return gclidFromUrl
  }

  // Then check localStorage
  try {
    const storedGclid = localStorage.getItem('_gclid')
    const timestamp = localStorage.getItem('_gclid_timestamp')

    if (storedGclid && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10)
      const ninetyDays = 90 * 24 * 60 * 60 * 1000

      if (age < ninetyDays) {
        return storedGclid
      } else {
        // Expired, clean up
        localStorage.removeItem('_gclid')
        localStorage.removeItem('_gclid_timestamp')
      }
    }
  } catch (e) {
    console.warn('Failed to get gclid from localStorage:', e)
  }

  return null
}

/**
 * Store fbclid to localStorage
 */
export function storeFbclid(fbclid: string): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem('_fbclid', fbclid)
    localStorage.setItem('_fbclid_timestamp', Date.now().toString())
  } catch (e) {
    console.warn('Failed to store fbclid:', e)
  }
}

/**
 * Get fbclid from localStorage or URL
 */
export function getFbclid(): string | null {
  if (typeof window === 'undefined') return null

  // First check URL params
  const urlParams = new URLSearchParams(window.location.search)
  const fbclidFromUrl = urlParams.get('fbclid')

  if (fbclidFromUrl) {
    storeFbclid(fbclidFromUrl)
    return fbclidFromUrl
  }

  // Then check localStorage
  try {
    const storedFbclid = localStorage.getItem('_fbclid')
    const timestamp = localStorage.getItem('_fbclid_timestamp')

    if (storedFbclid && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10)
      const ninetyDays = 90 * 24 * 60 * 60 * 1000

      if (age < ninetyDays) {
        return storedFbclid
      } else {
        localStorage.removeItem('_fbclid')
        localStorage.removeItem('_fbclid_timestamp')
      }
    }
  } catch (e) {
    console.warn('Failed to get fbclid from localStorage:', e)
  }

  return null
}

/**
 * Get GA4 client_id from gtag
 */
export function getGA4ClientId(): Promise<string | null> {
  return new Promise((resolve) => {
    if (
      typeof window === 'undefined' ||
      !window.gtag ||
      !GOOGLE_MEASUREMENT_ID
    ) {
      resolve(null)
      return
    }

    try {
      window.gtag(
        'get',
        GOOGLE_MEASUREMENT_ID,
        'client_id',
        (clientId: string) => {
          resolve(clientId)
        },
      )

      // Timeout after 1 second
      setTimeout(() => resolve(null), 1000)
    } catch (e) {
      console.warn('Failed to get GA4 client_id:', e)
      resolve(null)
    }
  })
}

/**
 * Collect all tracking parameters
 */
export async function collectTrackingParams(): Promise<TrackingParams> {
  const params: TrackingParams = {}

  // Parse attribution signals from cookies/URL synchronously when possible
  if (typeof document !== 'undefined') {
    try {
      const attribution = extractAttributionSignals(
        document.cookie,
        document.referrer,
        typeof window !== 'undefined' ? window.location.href : undefined,
      )
      params.gclid = attribution.gclid || params.gclid
      params.wbraid = attribution.wbraid || params.wbraid
      params.gbraid = attribution.gbraid || params.gbraid
      params.ga_client_id = attribution.ga_client_id
      params.ga_session_id = attribution.ga_session_id
      params.ga_session_number = attribution.ga_session_number
      params.utm_source = attribution.utm_source
      params.utm_medium = attribution.utm_medium
      params.utm_campaign = attribution.utm_campaign
      params.utm_term = attribution.utm_term
      params.utm_content = attribution.utm_content
      params.landing_page = attribution.page_url || params.landing_page
      params.referrer = attribution.referrer_url || params.referrer
    } catch (e) {
      console.warn('Failed to extract attribution signals from cookies', e)
    }
  }

  const gclid = getGclid()
  if (gclid) params.gclid = gclid

  const fbclid = getFbclid()
  if (fbclid) params.fbclid = fbclid

  const clientId = await getGA4ClientId()
  if (clientId) params.ga4_client_id = clientId

  if (typeof window !== 'undefined') {
    params.landing_page = window.location.href
    params.referrer = document.referrer
  }

  return params
}
